import { useTranslation } from 'react-i18next';
import { PenLine, Sparkles } from 'lucide-react';
import type { IdeaMode } from './types';

/**
 * The two doors above the trial dialog (Kai, 2026-09-09): "Inspirieren lassen" (lit by
 * default: chips + idea cards) and "Eigene Idee" (the box, the mic, three examples).
 * Door-sized on purpose — a thin tab bar reads as a filter and gets ignored. Persistent,
 * so the choice stays visible while the parent works; one idea box behind both.
 */
export default function ModeDoors({ prefix, mode, onChange }: { prefix: string; mode: IdeaMode; onChange: (mode: IdeaMode) => void }) {
  const { t } = useTranslation();
  const doors: { m: IdeaMode; Icon: typeof Sparkles; label: string; sub: string }[] = [
    { m: 'guided', Icon: Sparkles, label: t('idea.doorGuided'), sub: t('idea.doorGuidedSub') },
    { m: 'own', Icon: PenLine, label: t('idea.doorOwn'), sub: t('idea.doorOwnSub') },
  ];
  return (
    <div className="grid grid-cols-2 gap-3" role="group" data-testid={`${prefix}-doors`}>
      {doors.map(({ m, Icon, label, sub }) => {
        const active = m === mode;
        return (
          <button
            key={m}
            type="button"
            onClick={() => onChange(m)}
            aria-pressed={active}
            data-testid={`${prefix}-door-${m}`}
            className={`text-left rounded-2xl border-2 p-3 min-h-[76px] transition-colors ${
              active ? 'border-primary bg-primary/5' : 'border-stone-200 bg-white hover:border-primary/40'
            }`}
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Icon className={`w-4 h-4 ${active ? 'text-primary' : 'text-muted-foreground'}`} aria-hidden="true" />
              {label}
            </span>
            <span className="block text-xs text-muted-foreground mt-1 leading-snug">{sub}</span>
          </button>
        );
      })}
    </div>
  );
}
